import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TagEditor } from "@/features/sms/components/editor/TagEditor";
import { TagPaletteHorizontal } from "@/features/sms/components/editor/TagPaletteHorizontal";
import { GUEST_SMS_PLACEHOLDERS } from "@/features/sms/constants/guestSmsPlaceholders";
import type {
  PlaceholderConfig,
  TagEditorRef,
} from "@/features/sms/types/sms.types";
import {
  useCompanyGuestTemplate,
  useUpsertGuestTemplate,
  useDeleteGuestTemplate,
} from "../hooks/useCompanySettings";
import { GUEST_ORDER_TEMPLATE_TYPE } from "../services/companySettingsService";

interface GuestSmsTemplatePanelProps {
  companyGuid: string;
}

export function GuestSmsTemplatePanel({
  companyGuid,
}: GuestSmsTemplatePanelProps) {
  const { data, isLoading } = useCompanyGuestTemplate(companyGuid);
  const upsert = useUpsertGuestTemplate(companyGuid);
  const del = useDeleteGuestTemplate(companyGuid);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-xs text-gray-400">로딩 중...</span>
      </div>
    );
  }

  const hasOverride = !!data?.override;
  const effective = data?.override ?? data?.systemDefault ?? null;
  // override 생성/삭제(기본↔전용 전환) 시 key 변경 → 에디터 remount 로 초기값 재설정
  const editorKey = `${companyGuid}:${data?.override?.templateGuid ?? "default"}`;

  const handleRevert = () => {
    if (
      window.confirm(
        "업체 전용 템플릿을 삭제하고 시스템 기본 템플릿으로 복귀할까요?",
      )
    ) {
      del.mutate();
    }
  };

  return (
    <GuestTemplateEditor
      key={editorKey}
      hasOverride={hasOverride}
      initialTitle={effective?.title ?? ""}
      initialTemplate={effective?.template ?? ""}
      isSaving={upsert.isPending}
      isDeleting={del.isPending}
      onSave={(title, template) =>
        upsert.mutate({
          CompanyGuid: companyGuid,
          TemplateType: GUEST_ORDER_TEMPLATE_TYPE,
          Title: title.trim() || "게스트 접수완료 안내",
          Template: template,
        })
      }
      onRevert={handleRevert}
    />
  );
}

interface GuestTemplateEditorProps {
  hasOverride: boolean;
  initialTitle: string;
  initialTemplate: string;
  isSaving: boolean;
  isDeleting: boolean;
  onSave: (title: string, template: string) => void;
  onRevert: () => void;
}

function GuestTemplateEditor({
  hasOverride,
  initialTitle,
  initialTemplate,
  isSaving,
  isDeleting,
  onSave,
  onRevert,
}: GuestTemplateEditorProps) {
  const [title, setTitle] = useState(initialTitle);
  const [template, setTemplate] = useState(initialTemplate);
  const editorRef = useRef<TagEditorRef>(null);

  const handleInsertTag = (config: PlaceholderConfig) => {
    editorRef.current?.insertPlaceholder(config);
  };

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">게스트 등록 SMS</h4>
        <p className="text-xs text-gray-500">
          미등록 화주의 주문 등록 완료 시 발송되는 SMS 템플릿
        </p>
      </div>

      {/* 적용 상태 배너 */}
      <div
        className={`rounded-md px-3 py-2 text-xs ${
          hasOverride
            ? "border border-green-100 bg-green-50 text-green-700"
            : "border border-blue-100 bg-blue-50 text-blue-700"
        }`}
      >
        {hasOverride
          ? "이 업체 전용 템플릿이 적용 중입니다."
          : "시스템 기본 템플릿이 적용 중입니다. 저장하면 이 업체 전용으로 생성됩니다."}
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-700">
          제목
        </label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="템플릿 제목"
          className="h-8 text-xs"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-700">
          내용
        </label>
        <div className="overflow-hidden rounded-md border border-gray-200">
          <TagEditor
            ref={editorRef}
            value={template}
            onChange={setTemplate}
            placeholders={GUEST_SMS_PLACEHOLDERS}
            minHeight="180px"
            placeholderText="메시지를 입력하세요. / 를 입력하거나 아래 태그를 눌러 변수를 삽입할 수 있습니다."
            className="rounded-none border-0"
          />
          <TagPaletteHorizontal
            placeholders={GUEST_SMS_PLACEHOLDERS}
            onSelect={handleInsertTag}
          />
        </div>
        <p className="mt-1 text-[11px] text-gray-400">
          변수는 발송 시 실제 값으로 치환됩니다.
        </p>
      </div>

      <div className="flex items-center justify-between pt-1">
        {hasOverride ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={isDeleting}
            onClick={onRevert}
            className="h-7 px-2 text-xs text-red-600 hover:bg-red-50"
          >
            기본으로 복귀
          </Button>
        ) : (
          <span />
        )}
        <Button
          type="button"
          size="sm"
          disabled={isSaving || !template.trim()}
          onClick={() => onSave(title, template)}
          className="h-7 bg-blue-600 px-3 text-xs font-bold hover:bg-blue-700"
        >
          {isSaving ? "저장 중..." : "업체 전용으로 저장"}
        </Button>
      </div>
    </div>
  );
}
